import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { isTieredSubscriptionProductVariant, isTiersProductVariant } from '@weft/services';
import type { Currency, TieredSubscriptionProductVariantInput, TiersProductVariantInput } from '@weft/types';
import { withDefault } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import React, { useMemo } from 'react';
import { Price } from '../../../../../../../../components/design/Price';
import { getToDisplayValue } from '../../../../UsageCharges/usage-charges-components/UsageProductVariantTableEdit/edit-tiers-table-columns';

type LivePreviewTiersTableProps = {
  productVariant: TiersProductVariantInput | TieredSubscriptionProductVariantInput;
  currency: Currency;
  isRevShare?: boolean;
};

export const LivePreviewTiersTable: React.FC<LivePreviewTiersTableProps> = ({ productVariant, currency, isRevShare }) => {
  const tiers = withDefault(productVariant.pricing.priceConfiguration?.tiers, []);

  const isFlatRateVariant = useMemo(() => {
    if (isTiersProductVariant(productVariant)) {
      return productVariant.pricing.priceConfiguration?.isFlatRate;
    }

    return isTieredSubscriptionProductVariant(productVariant);
  }, [productVariant]);

  return (
    <TableContainer>
      <Table style={{
        tableLayout: 'fixed',
      }}
      >
        <TableHead>
          <TableRow>
            <TableCell className="p-0 pb-1.5 text-[10px] font-light leading-3 text-[#576063]">From</TableCell>
            <TableCell
              className="p-0 pb-1.5 text-[10px] font-light leading-3 text-[#576063]"
              align="center"
            >
              To
            </TableCell>
            {!isTieredSubscriptionProductVariant(productVariant) && (
              <TableCell
                className="p-0 pb-1.5 text-[10px] font-light leading-3 text-[#576063]"
                align={isFlatRateVariant ? 'center' : 'right'}
              >
                {isRevShare ? 'Revenue Share' : 'Per Unit'}
              </TableCell>
            )}
            {isFlatRateVariant && (
              <TableCell
                className="p-0 pb-1.5 text-[10px] font-light leading-3 text-[#576063]"
                align="right"
              >
                {isTieredSubscriptionProductVariant(productVariant) ? 'Fixed Rate' : 'Flat Rate'}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tiers.map((tier) => (
            <TableRow
              key={tier.from}
              sx={{
                '&:last-child td, &:last-child th': {
                  borderBottom: 0,
                },
                'td, th': {
                  position: 'relative',
                },
                'td:before, th:before': {
                  content: '""',
                  height: '16px',
                  width: '1px',
                  backgroundColor: '#E1E6E8',
                  position: 'absolute',
                  right: 0,
                  top: '8px',
                },
                'td:last-child:before': {
                  content: '""',
                  height: 0,
                  width: 0,
                  backgroundColor: 'transparent',
                  position: 'absolute',
                },
              }}
            >
              <TableCell
                component="th"
                scope="row"
                className="px-0 py-2 text-xs font-light leading-4 text-[#606A6E]"
              >
                {formatNumber(tier.from, 0)}
              </TableCell>
              <TableCell
                className="px-0 py-2 text-xs font-light leading-4 text-[#606A6E]"
                align="center"
              >
                {getToDisplayValue(withDefault(tier.to, 0))}
              </TableCell>
              {!isTieredSubscriptionProductVariant(productVariant) && (
                <TableCell
                  className="px-0 py-2 text-xs font-light leading-4 text-[#606A6E]"
                  align={isFlatRateVariant ? 'center' : 'right'}
                >
                  {(
                    isRevShare ? (
                      <Typography
                        className="text-xs font-light leading-4 text-[#606A6E]"
                        align={isFlatRateVariant ? 'center' : 'right'}
                      >
                        {withDefault(tier.price, 0)}
                        %
                      </Typography>
                    ) : (
                      <Price
                        currency={currency}
                        align={isFlatRateVariant ? 'center' : 'right'}
                        className="text-xs font-light leading-4 text-[#606A6E]"
                      >
                        {withDefault(tier.price, 0)}
                      </Price>
                    )
                  )}
                </TableCell>
              )}
              {isFlatRateVariant && (
                <TableCell
                  className="px-0 py-2 text-xs font-light leading-4 text-[#606A6E]"
                  align="right"
                >
                  <Price
                    currency={currency}
                    align="right"
                    className="text-xs font-light leading-4 text-[#606A6E]"
                  >
                    {withDefault(tier.flatRate, 0)}
                  </Price>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};